'use client'

import { keepPreviousData, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useDebouncedValue } from '../useDebouncedValue/useDebouncedValue'

/** Grid state handed to the `query` factory on every change (page is 0-based). */
export interface ServerGridParams {
    page: number
    pageSize: number
    sortModel: GridSortModel
    filterModel: GridFilterModel
    search: string
}

/**
 * Query options for one grid page. The key type is left open (`any`): generated factories return
 * precisely-typed (DataTag) keys, which don't widen to `QueryKey` in the queryFn's context, and the
 * hook never reads the key itself.
 */
export type ServerGridQueryOptions<TData, TError> = UseQueryOptions<TData, TError, TData, any>

export interface UseServerGridOptions<TData, TRow, TError = Error> {
    /**
     * Build the query for the current grid state — normally a generated
     * `getXxxOptions({ path, query })` from `@/generated/api-client/@tanstack/react-query.gen`.
     * Its query key carries the request params, so each page/sort/filter/search gets its own cache entry.
     */
    query: (params: ServerGridParams) => ServerGridQueryOptions<TData, TError>
    /** Pull the page rows and the total row count out of the response. */
    select: (data: TData) => { rows: TRow[]; total: number }
    enabled?: boolean
    initialPageSize?: number
    pageSizeOptions?: number[]
    initialSortModel?: GridSortModel
    initialFilterModel?: GridFilterModel
    /** Controlled filter model — filter state lives outside the hook; changes go to `onFilterModelChange` (page still resets to 0). */
    filterModel?: GridFilterModel
    onFilterModelChange?: (model: GridFilterModel) => void
    /** Debounce sort/filter/search-driven refetches by this many ms (pagination stays immediate). */
    debounceMs?: number
    /** Server-side sorting: wires sortModel/sortingMode into `dataGridProps` (default true). */
    serverSorting?: boolean
    /** Server-side filtering: wires filterModel/filterMode into `dataGridProps` (default true). */
    serverFiltering?: boolean
}

const EMPTY_FILTER: GridFilterModel = { items: [] }

/**
 * Server-paginated `DataGridPro` on top of a generated react-query options factory.
 *
 * Owns pagination/sort/filter/search state, resets to page 0 when sort, filter or search
 * change, debounces those (never pagination), and keeps the previous page's data while the
 * next one loads — otherwise MUI's rowCount clamp snaps the grid back to page 0 mid-navigation.
 *
 *   const { rows, dataGridProps } = useServerGrid({
 *       query: ({ page, pageSize, search }) => getAssessmentTemplatesOptions({ query: { companySlug, page: String(page), limit: String(pageSize), search } }),
 *       select: (res) => ({ rows: res.data.AssessmentTemplates, total: res.data.total }),
 *   })
 *   <DataGridPro rows={rows} columns={columns} {...dataGridProps} />
 */
export function useServerGrid<TData, TRow = unknown, TError = Error>({
    query: buildQuery,
    select,
    enabled = true,
    initialPageSize = 25,
    pageSizeOptions = [10, 25, 50],
    initialSortModel,
    initialFilterModel,
    filterModel: controlledFilterModel,
    onFilterModelChange: controlledOnFilterModelChange,
    debounceMs = 0,
    serverSorting = true,
    serverFiltering = true,
}: UseServerGridOptions<TData, TRow, TError>) {
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: initialPageSize })
    const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel ?? [])
    const [internalFilterModel, setInternalFilterModel] = useState<GridFilterModel>(initialFilterModel ?? EMPTY_FILTER)
    const [searchValue, setSearchValue] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')

    const filterModel = controlledFilterModel ?? internalFilterModel
    const setFilterModel = useCallback<Dispatch<SetStateAction<GridFilterModel>>>(
        (update) => {
            const next = typeof update === 'function' ? update(controlledFilterModel ?? internalFilterModel) : update
            if (controlledFilterModel !== undefined) controlledOnFilterModelChange?.(next)
            else setInternalFilterModel(next)
        },
        [controlledFilterModel, controlledOnFilterModelChange, internalFilterModel]
    )

    const resetToFirstPage = useCallback(() => {
        setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }))
    }, [])

    const onSortModelChange = useCallback(
        (model: GridSortModel) => {
            setSortModel(model)
            resetToFirstPage()
        },
        [resetToFirstPage]
    )

    const onFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            setFilterModel(model)
            resetToFirstPage()
        },
        [setFilterModel, resetToFirstPage]
    )

    const onSearchSubmit = useCallback(
        (value: string) => {
            setAppliedSearch(value.trim())
            resetToFirstPage()
        },
        [resetToFirstPage]
    )

    const onSearchClear = useCallback(() => {
        setSearchValue('')
        setAppliedSearch('')
        resetToFirstPage()
    }, [resetToFirstPage])

    // Sort/filter/search are debounced as one value so rapid adjustments batch into a single fetch.
    const debounced = useDebouncedValue(JSON.stringify({ sortModel, filterModel, search: appliedSearch }), debounceMs)
    const {
        sortModel: querySort,
        filterModel: queryFilter,
        search,
    } = JSON.parse(debounced) as {
        sortModel: GridSortModel
        filterModel: GridFilterModel
        search: string
    }

    const query: UseQueryResult<TData, TError> = useQuery({
        ...buildQuery({ page: paginationModel.page, pageSize: paginationModel.pageSize, sortModel: querySort, filterModel: queryFilter, search }),
        enabled,
        placeholderData: keepPreviousData,
    })

    // Stable rows identity between renders — a fresh `?? []` would bust consumers' useMemo keyed on rows.
    const rowsRef = useRef<TRow[]>([])
    const selected = query.data !== undefined ? select(query.data) : undefined
    if (selected) rowsRef.current = selected.rows
    const totalRows = selected?.total ?? 0

    return {
        query,
        rows: rowsRef.current,
        totalRows,
        paginationModel,
        setPaginationModel,
        sortModel,
        setSortModel,
        filterModel,
        setFilterModel,
        searchValue,
        setSearchValue,
        appliedSearch,
        onSearchSubmit,
        onSearchClear,
        dataGridProps: {
            paginationMode: 'server' as const,
            ...(serverSorting ? { sortingMode: 'server' as const, sortModel, onSortModelChange } : {}),
            ...(serverFiltering ? { filterMode: 'server' as const, filterModel, onFilterModelChange } : {}),
            paginationModel,
            onPaginationModelChange: setPaginationModel,
            rowCount: totalRows,
            loading: query.isLoading,
            pageSizeOptions,
        },
    }
}
